import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './CommunicationHub.css';

const CommunicationHub = () => {
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token;
  const [view, setView] = useState('announcements'); // 'announcements', 'messages'
  const [announcements, setAnnouncements] = useState([]);
  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);

  // Announcement modal
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    priority: 'normal',
    target_audience: 'all',
    scheduled_for: '',
    notify_email: false,
    notify_sms: false
  });

  // Which extra delivery channels are configured on the server
  const [channels, setChannels] = useState({ email: false, sms: false });

  // Message modal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [newMessageForm, setNewMessageForm] = useState({
    target_type: 'class',   // 'gym' | 'class' | 'athletes' | 'staff'
    class_id: '',
    athlete_ids: [],
    subject: '',
    message: '',
    include_parents: true,
    include_athletes: true,
    notify_email: false,
    notify_sms: false
  });
  const [recipients, setRecipients] = useState({ athletes: [], classes: [] });
  const [sendingMessage, setSendingMessage] = useState(false);

  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    // Get current user
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUserId(user.id);
    setUserRole(user.role);
    
    fetchAnnouncements();
    fetchMessageThreads();
    fetchRecipients();
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/notify/channels`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setChannels(await res.json());
    } catch { /* channels stay off */ }
  };

  const fetchRecipients = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/messages/recipients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecipients({ athletes: data.athletes || [], classes: data.classes || [] });
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const fetchMessageThreads = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/messages/threads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessageThreads(data);
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(announcementForm)
      });
      
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const n = data.notified;
        toast.success('Announcement posted!' +
          (data.notified_in_app ? ` — ${data.notified_in_app} notified` : '') +
          (n ? `, emailed ${n.emailed}, texted ${n.texted}` : ''));
        setShowAnnouncementModal(false);
        setAnnouncementForm({
          title: '',
          message: '',
          priority: 'normal',
          target_audience: 'all',
          scheduled_for: '',
          notify_email: false,
          notify_sms: false
        });
        fetchAnnouncements();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Failed to create announcement');
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
      toast.error('Failed to create announcement');
    }
  };

  const markAsRead = async (announcementId) => {
    try {
      await fetch(`${API_BASE_URL}/api/announcements/${announcementId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAnnouncements();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const addReaction = async (announcementId, reactionType) => {
    try {
      await fetch(`${API_BASE_URL}/api/announcements/${announcementId}/react`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reaction_type: reactionType })
      });
      fetchAnnouncements();
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();

    if (newMessageForm.target_type === 'class' && !newMessageForm.class_id) {
      toast.error('Pick a class to message');
      return;
    }
    if (newMessageForm.target_type === 'athletes' && newMessageForm.athlete_ids.length === 0) {
      toast.error('Select at least one athlete');
      return;
    }

    setSendingMessage(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/messages/compose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          target_type: newMessageForm.target_type,
          class_id: newMessageForm.class_id || undefined,
          athlete_ids: newMessageForm.athlete_ids,
          subject: newMessageForm.subject,
          message: newMessageForm.message,
          include_parents: newMessageForm.include_parents,
          include_athletes: newMessageForm.include_athletes,
          notify_email: newMessageForm.notify_email,
          notify_sms: newMessageForm.notify_sms
        })
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const reach = data.recipients_in_app || 0;
        const n = data.notified;
        if (data.threads_created > 0) {
          toast.success(
            (newMessageForm.target_type === 'staff'
              ? `Sent to ${reach} staff`
              : `Sent to ${data.threads_created} ${data.threads_created === 1 ? 'family' : 'families'} (${reach} in-app)`) +
            (n ? ` · emailed ${n.emailed}, texted ${n.texted}` : '')
          );
        } else {
          toast.error('No one with an app account to message for that selection');
        }
        if (data.skipped_no_parent && data.skipped_no_parent.length > 0) {
          toast(`No app account yet for: ${data.skipped_no_parent.join(', ')}`, { icon: '⚠️' });
        }
        setShowMessageModal(false);
        setNewMessageForm({ target_type: 'class', class_id: '', athlete_ids: [], subject: '', message: '', include_parents: true, include_athletes: true, notify_email: false, notify_sms: false });
        fetchMessageThreads();
      } else {
        toast.error(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const toggleAthleteRecipient = (athleteId) => {
    setNewMessageForm(prev => ({
      ...prev,
      athlete_ids: prev.athlete_ids.includes(athleteId)
        ? prev.athlete_ids.filter(id => id !== athleteId)
        : [...prev.athlete_ids, athleteId]
    }));
  };

  const loadThread = async (threadId) => {
    setSelectedThread(threadId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/messages/threads/${threadId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async (threadId, content) => {
    try {
      await fetch(`${API_BASE_URL}/api/messages/threads/${threadId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content })
      });
      loadThread(threadId);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#f44336';
      case 'high': return '#FF9800';
      case 'normal': return '#2196F3';
      case 'low': return '#9E9E9E';
      default: return '#2196F3';
    }
  };

  return (
    <div className="communication-hub">
      <div className="header">
        <h1>Communication Hub</h1>
        {(userRole === 'admin' || userRole === 'coach') && (
        <div className="quick-actions">
          <button className="btn-primary" onClick={() => setShowAnnouncementModal(true)}>
            New Announcement
          </button>
          <button className="btn-primary" onClick={() => setShowMessageModal(true)}>
            New Message
          </button>
        </div>
        )}
      </div>

      {/* View Tabs */}
      <div className="view-tabs">
        <button 
          className={view === 'announcements' ? 'active' : ''} 
          onClick={() => setView('announcements')}
        >
          📢 Announcements
        </button>
        <button 
          className={view === 'messages' ? 'active' : ''} 
          onClick={() => setView('messages')}
        >
          💬 Messages {messageThreads.filter(t => t.unread_count > 0).length > 0 &&
            <span className="badge">{messageThreads.filter(t => t.unread_count > 0).length}</span>}
        </button>
      </div>

      {/* Announcements View */}
      {view === 'announcements' && (
        <div className="announcements-feed">
          {announcements.map(announcement => (
            <div 
              key={announcement.id} 
              className="announcement-card"
              style={{ borderLeftColor: getPriorityColor(announcement.priority) }}
            >
              <div className="announcement-header">
                <div className="announcement-meta">
                  <h3>{announcement.title}</h3>
                  <span className="priority-badge" style={{ background: getPriorityColor(announcement.priority) }}>
                    {announcement.priority}
                  </span>
                </div>
                <span className="announcement-date">
                  {new Date(announcement.created_at).toLocaleDateString()}
                </span>
              </div>
              
              <div className="announcement-content">
                {announcement.message}
              </div>

              <div className="announcement-footer">
                <div className="reactions">
                  <button onClick={() => addReaction(announcement.id, 'like')}>👍 {announcement.like_count || 0}</button>
                  <button onClick={() => addReaction(announcement.id, 'love')}>❤️ {announcement.love_count || 0}</button>
                  <button onClick={() => addReaction(announcement.id, 'thanks')}>🙏 {announcement.thanks_count || 0}</button>
                </div>
                <div className="announcement-stats">
                  <span>{announcement.read_count || 0} reads</span>
                  {!announcement.is_read && (
                    <button 
                      className="btn-small btn-secondary" 
                      onClick={() => markAsRead(announcement.id)}
                    >
                      Mark as Read
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {announcements.length === 0 && (
            <div className="empty-state">
              No announcements yet. Create one to communicate with your team!
            </div>
          )}
        </div>
      )}

      {/* Messages View */}
      {view === 'messages' && (
        <div className="messages-view">
          <div className="threads-sidebar">
            <h3>Conversations</h3>
            {messageThreads.map(thread => (
              <div 
                key={thread.id}
                className={`thread-item ${selectedThread === thread.id ? 'active' : ''} ${thread.unread_count > 0 ? 'unread' : ''}`}
                onClick={() => loadThread(thread.id)}
              >
                <div className="thread-subject">{thread.subject}</div>
                {thread.athlete_name && (
                  <div className="thread-athlete" style={{ fontSize: 12, color: '#888' }}>
                    {thread.athlete_name}'s family
                  </div>
                )}
                <div className="thread-preview">{thread.last_message_preview}</div>
                <div className="thread-meta">
                  <span>{new Date(thread.last_message_at).toLocaleDateString()}</span>
                  {thread.unread_count > 0 && (
                    <span className="unread-badge">{thread.unread_count}</span>
                  )}
                </div>
              </div>
            ))}

            {messageThreads.length === 0 && (
              <div className="empty-threads">
                No messages yet. Start a conversation!
              </div>
            )}
          </div>

          <div className="messages-panel">
            {selectedThread ? (
              <>
                <div className="messages-list">
                  {messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`message ${msg.sender_id === currentUserId ? 'sent' : 'received'}`}
                    >
                      <div className="message-sender">{msg.sender_name}</div>
                      <div className="message-content">{msg.content}</div>
                      <div className="message-time">
                        {new Date(msg.sent_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="message-input">
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const input = e.target.message;
                    if (input.value.trim()) {
                      sendMessage(selectedThread, input.value);
                      input.value = '';
                    }
                  }}>
                    <input 
                      type="text" 
                      name="message" 
                      placeholder="Type a message..." 
                    />
                    <button type="submit" className="btn-primary">Send</button>
                  </form>
                </div>
              </>
            ) : (
              <div className="empty-state">
                Select a conversation to view messages
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Announcement Modal */}
      {showAnnouncementModal && (
        <div className="modal-overlay" onClick={() => setShowAnnouncementModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Announcement</h2>
              <button className="close-btn" onClick={() => setShowAnnouncementModal(false)}>×</button>
            </div>

            <form onSubmit={handleCreateAnnouncement}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={announcementForm.title}
                  onChange={e => setAnnouncementForm({...announcementForm, title: e.target.value})}
                  required
                  placeholder="e.g., Gym Closure This Weekend"
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <textarea
                  value={announcementForm.message}
                  onChange={e => setAnnouncementForm({...announcementForm, message: e.target.value})}
                  required
                  placeholder="Type your announcement here..."
                  rows="6"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    value={announcementForm.priority}
                    onChange={e => setAnnouncementForm({...announcementForm, priority: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Target Audience *</label>
                  <select
                    value={announcementForm.target_audience}
                    onChange={e => setAnnouncementForm({...announcementForm, target_audience: e.target.value})}
                  >
                    <option value="all">All</option>
                    <option value="parents">Parents Only</option>
                    <option value="athletes">Athletes Only</option>
                    <option value="staff">Staff Only</option>
                  </select>
                </div>
              </div>

              <div className="notify-channels">
                <span className="notify-label">Also send by</span>
                <label className={!channels.email ? 'disabled' : ''}>
                  <input type="checkbox" disabled={!channels.email} checked={announcementForm.notify_email}
                    onChange={e => setAnnouncementForm({ ...announcementForm, notify_email: e.target.checked })} />
                  📧 Email {!channels.email && <small>(not set up)</small>}
                </label>
                <label className={!channels.sms ? 'disabled' : ''}>
                  <input type="checkbox" disabled={!channels.sms} checked={announcementForm.notify_sms}
                    onChange={e => setAnnouncementForm({ ...announcementForm, notify_sms: e.target.checked })} />
                  💬 Text {!channels.sms && <small>(not set up)</small>}
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAnnouncementModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Publish Announcement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Message Modal */}
      {showMessageModal && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Message</h2>
              <button className="close-btn" onClick={() => setShowMessageModal(false)}>×</button>
            </div>

            <form onSubmit={handleCreateThread}>
              <div className="form-group">
                <label>Send to *</label>
                <div className="segmented" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  {[
                    { key: 'class', label: '👥 A class' },
                    { key: 'gym', label: '🏟️ Whole gym' },
                    { key: 'athletes', label: '🙋 Specific athletes' },
                    { key: 'staff', label: '🧑‍🏫 Staff / Coaches' }
                  ].map(opt => (
                    <button
                      type="button"
                      key={opt.key}
                      className={newMessageForm.target_type === opt.key ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => setNewMessageForm({ ...newMessageForm, target_type: opt.key })}
                      style={{ flex: 1 }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {newMessageForm.target_type === 'staff' ? (
                  <small>Goes to every coach & admin on your team — each gets it in-app + a notification.</small>
                ) : (
                  <small>Recipients get it in-app + a notification (and email/text if you toggle it and they've added contact info).</small>
                )}
              </div>

              {newMessageForm.target_type !== 'staff' && (
                <div className="notify-channels" style={{ background: '#eff6ff', borderColor: '#bfdbfe', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="notify-label" style={{ color: '#1d4ed8' }}>Include</span>
                    <label className={newMessageForm.include_athletes ? 'disabled' : ''}>
                      <input type="checkbox"
                        checked={newMessageForm.include_parents || newMessageForm.include_athletes}
                        disabled={newMessageForm.include_athletes}
                        onChange={e => setNewMessageForm({ ...newMessageForm, include_parents: e.target.checked })} />
                      👪 Parents
                    </label>
                    <label>
                      <input type="checkbox" checked={newMessageForm.include_athletes}
                        onChange={e => setNewMessageForm({
                          ...newMessageForm,
                          include_athletes: e.target.checked,
                          include_parents: e.target.checked ? true : newMessageForm.include_parents
                        })} />
                      🙋 Athletes
                    </label>
                  </div>
                  {newMessageForm.include_athletes && (
                    <small style={{ color: '#1d4ed8' }}>🔒 Parents are always included in any chat with an athlete.</small>
                  )}
                </div>
              )}

              {newMessageForm.target_type === 'gym' && (
                <div className="form-group">
                  <p style={{ margin: 0 }}>
                    Messaging the parents of all <strong>{recipients.athletes.length}</strong> athletes on the team.
                  </p>
                </div>
              )}

              {newMessageForm.target_type === 'class' && (
                <div className="form-group">
                  <label>Class *</label>
                  <select
                    value={newMessageForm.class_id}
                    onChange={e => setNewMessageForm({ ...newMessageForm, class_id: e.target.value })}
                    required
                  >
                    <option value="">Select a class…</option>
                    {recipients.classes.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.athlete_ids.length} enrolled)
                      </option>
                    ))}
                  </select>
                  {recipients.classes.length === 0 && (
                    <small>No classes yet — create one in Class Management.</small>
                  )}
                </div>
              )}

              {newMessageForm.target_type === 'athletes' && (
                <div className="form-group">
                  <label>Athletes *</label>
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 8, padding: 8 }}>
                    {recipients.athletes.map(a => (
                      <label
                        key={a.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={newMessageForm.athlete_ids.includes(a.id)}
                          onChange={() => toggleAthleteRecipient(a.id)}
                          style={{ width: 16, height: 16, flexShrink: 0, margin: 0, padding: 0 }}
                        />
                        <span style={{ flex: 1 }}>{a.name}</span>
                        {a.parents.length > 0 ? (
                          <span style={{ fontSize: 12, color: '#666' }}>
                            {a.parents.map(p => p.name).join(', ')}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#c0392b' }}>no parent linked</span>
                        )}
                      </label>
                    ))}
                    {recipients.athletes.length === 0 && (
                      <small>No athletes found.</small>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={newMessageForm.subject}
                  onChange={e => setNewMessageForm({...newMessageForm, subject: e.target.value})}
                  required
                  placeholder="Message subject"
                />
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={newMessageForm.message}
                  onChange={e => setNewMessageForm({...newMessageForm, message: e.target.value})}
                  required
                  placeholder="Type your message..."
                  rows="5"
                />
              </div>

              <div className="notify-channels">
                <span className="notify-label">Also send by</span>
                <label className={!channels.email ? 'disabled' : ''}>
                  <input type="checkbox" disabled={!channels.email} checked={newMessageForm.notify_email}
                    onChange={e => setNewMessageForm({ ...newMessageForm, notify_email: e.target.checked })} />
                  📧 Email {!channels.email && <small>(not set up)</small>}
                </label>
                <label className={!channels.sms ? 'disabled' : ''}>
                  <input type="checkbox" disabled={!channels.sms} checked={newMessageForm.notify_sms}
                    onChange={e => setNewMessageForm({ ...newMessageForm, notify_sms: e.target.checked })} />
                  💬 Text {!channels.sms && <small>(not set up)</small>}
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowMessageModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={sendingMessage}>
                  {sendingMessage ? 'Sending…' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default CommunicationHub;
